import { FC, ReactNode, useRef } from 'react';

import Button, { ButtonSize, ButtonVariant } from './Button';

export type FileTriggerProps = {
  accept?: string;
  onFile: (file: File) => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
};

/**
 * A button that opens the file picker.
 *
 * The native input is hidden and cleared before every open and after every
 * change, so choosing the same file twice still fires onFile. Pages no longer
 * hand-build a label-wrapped input to get the same behaviour.
 */
const FileTrigger: FC<FileTriggerProps> = ({ accept, onFile, disabled = false, variant, size, children, className }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    // Clear first: a file input only fires change when its value changes.
    input.value = '';
    input.click();
  };

  return (
    <>
      <Button variant={variant} size={size} onClick={openPicker} disabled={disabled} className={className}>
        {children}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        disabled={disabled}
        onChange={(event) => {
          const input = event.currentTarget;
          const file = input.files?.[0];
          if (file) onFile(file);
          input.value = '';
        }}
      />
    </>
  );
};

export default FileTrigger;
